"use client";
import { usePathname, useRouter } from "next/navigation";
import { useSearchContext } from "@/lib/hooks/searchContext/searchStore";
import { useEffect, useRef, useState } from "react";
import SearchButton from "@/components/SearchButton";
import SearchHeader from "@/components/layout/header/search/header";
import { cn } from "@/lib/utils";
import NavbarLogo from "@/components/layout/header/navbar-logo";
import NavbarSidebarAction from "@/components/layout/header/navbar-sidebar-action";
import { useTranslation } from "@/hooks/translationContext";
import dynamic from "next/dynamic";
import MagnifyingGlassIcon from "@heroicons/react/24/outline/MagnifyingGlassIcon";
import MapPinIcon from "@heroicons/react/24/outline/MapPinIcon";
import Bars3Icon from "@heroicons/react/24/outline/Bars3Icon";
import { useViewMode } from "@/context/ViewModeContext";
import { useVenueListPage } from "@/hooks/listPageContext";
import { useSite } from "@/lib/hooks/site";
import { useNearbyNavigation } from "@/hooks/useNearbyNavigation";
import { useCurrentDeviceContext } from "@/context/DeviceContext";
import { ROUTES } from "@/@core/constants/routes";

const SearchContent = dynamic(() => import("@/components/layout/header/search/content"));

export default function NavbarSearchButton() {
  const { isOpen, setIsOpen } = useSearchContext();
  const [show, setShow] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { t } = useTranslation();
  const modalRef = useRef<any>(null);
  const ref = useRef<any>(null);
  const { viewMode, toggleViewMode, setViewMode } = useViewMode();
  const { isVenueListPage } = useVenueListPage();
  const siteData = useSite();
  const { goToNearby } = useNearbyNavigation();
  const { device } = useCurrentDeviceContext();
  const isMobile = device === "mobile";

  const isMapView = viewMode === "map";
  // Mobile search row is not offered on the quote flow, same as before
  const isMobileSearchBlocked = pathname === ROUTES.CONTACT_WITH_VENUES;

  const handleMapToggle = () => {
    if (isVenueListPage) {
      toggleViewMode();
    } else {
      const generalListRoute = siteData?.setting?.routes?.generalList;
      if (generalListRoute) {
        router.push(`/${generalListRoute}`);
        // Set view mode after navigation starts
        setTimeout(() => {
          setViewMode("map");
        }, 0);
      }
    }
  };

  const getBlurStyles = () => {
    if (isOpen) {
      return "fixed h-[200px] left-0 right-0 top-0 bg-primary/95 w-full h-full opacity-100 z-40";
    }

    return "w-full";
  };

  useEffect(() => {
    // Warm up the search modal chunk so the first open is instant.
    import("@/components/layout/header/search/content");
  }, []);

  useEffect(() => {
    const headerRef = document.getElementById("site-navigation");

    if (isOpen) {
      headerRef?.style.setProperty("z-index", "9999");
    } else {
      headerRef?.style.setProperty("z-index", "30");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      ref?.current?.classList?.remove("search-click-input");
      setShow(false);
    }

    if (isOpen) {
      if (ref) ref.current?.classList.add("search-click-input");

      setTimeout(() => {
        setShow(true);
      }, 100);
    }
  }, [isOpen]);

  const closeModal = () => {
    const modal = modalRef.current;
    if (!modal) return;

    modal.classList.add("closing");
    setShow(false);

    setTimeout(function () {
      setIsOpen(false);
    }, 120);
  };

  const handleOutsideClick = (event: any) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      closeModal();
    }
  };

  useEffect(() => {
    // Only enable outside click on desktop
    if (typeof window !== 'undefined' && window.innerWidth >= 768) {
      if (isOpen) {
        document.addEventListener("mousedown", handleOutsideClick);
      } else {
        document.removeEventListener("mousedown", handleOutsideClick);
      }
    
      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }
  }, [isOpen]);

  const onSearchClick = () => {
    setIsOpen(!isOpen);
    ref.current.classList.add("search-click-input");
    setTimeout(() => {
      setShow(true);
    }, 100);
  };

  return (
    <div id="navbar-search-button" className={cn(`${isOpen ? "!z-[99]" : ""}`)}>
      <div className={cn(getBlurStyles(), "md:block hidden")} />

      <div className={cn(`${isOpen ? "search-modal" : ""} w-full !z-[99]`)}>
        <div className={`max-md:w-full`}>
          <div className={cn("w-full ", isOpen ? "md:hidden" : "")}>
            {/* Desktop Search Button - Original */}
            {!isMobile && pathname !== "/" && (
              <div className="md:w-[440px] w-full">
                <SearchButton
                  ref={ref}
                  type={2}
                  compact
                  onClick={onSearchClick}
                  placeholder={t("header-quicksearch-placeholder", {
                    defaultValue: "Mekan Arayın",
                  })}
                  id="header-input"
                />
              </div>
            )}

            {/* Mobile Search - Figma Design */}
            {isMobile && !isMobileSearchBlocked && pathname !== "/" && (
              <div
                className={cn(
                  "py-2 w-full items-center gap-3",
                  isOpen ? "hidden" : "flex",
                )}
              >
                {/* Search Input - Figma Design */}
                <button
                  ref={ref}
                  onClick={onSearchClick}
                  className="flex-1 flex items-center gap-2 px-4 py-3 bg-white border border-[#e8e8e8] rounded-full shadow-[0px_0px_6px_0px_rgba(88,88,88,0.25)]"
                >
                  <MagnifyingGlassIcon className="w-4 h-4 text-black" />
                  <span className="text-sm text-black font-medium tracking-[-0.14px]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {t("header-quicksearch-placeholder", {
                      defaultValue: "Merkez Ara",
                    })}
                  </span>
                </button>

                {/* Yakındakiler Button */}
                <button
                  onClick={goToNearby}
                  className="flex flex-col items-center gap-1 px-2 py-1"
                >
                  <MapPinIcon className="w-4 h-4 text-[#cd0241]" />
                  <span className="text-xs font-medium text-[#cd0241] tracking-[-0.14px] whitespace-nowrap" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {t("nearby-button", { defaultValue: "Yakındakiler" })}
                  </span>
                </button>

                {/* Harita/Listeleme Toggle Button */}
                <button
                  onClick={handleMapToggle}
                  className="flex flex-col items-center gap-1 px-2 py-1"
                >
                  {isMapView ? (
                    <Bars3Icon className="w-4 h-4 text-black" />
                  ) : (
                    <MapPinIcon className="w-4 h-4 text-black" />
                  )}
                  <span className="text-xs font-medium text-black tracking-[-0.14px] whitespace-nowrap" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    {isMapView
                      ? t("list-button", { defaultValue: "Listeleme" })
                      : t("map-button", { defaultValue: "Harita" })}
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Search overlay: only mounted while open, so its logo/search header
              are not duplicated into every page's markup. */}
          <div
            ref={modalRef}
            className={cn(
              `!z-[9999] flex-col items-center justify-center w-full ${isOpen ? "flex" : "hidden"}`,
            )}
          >
            {isOpen && (
              <>
                <div className="flex justify-between items-center w-full pt-4 md:h-28 container">
                  <NavbarLogo />
                  {!isMobile && (
                    <div className="flex">
                      <SearchHeader />
                    </div>
                  )}
                  <NavbarSidebarAction />
                </div>

                {isMobile && (
                  <div className="flex mt-4">
                    <SearchHeader />
                  </div>
                )}

                {show && <SearchContent />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
